import { tuiClamp, TuiDocumentSelectionException, tuiIsNodeIn, tuiParseColor } from '@taiga-ui/cdk';
import { Node } from 'prosemirror-model';
import { getHTMLFromFragment } from '@tiptap/core';

function tuiDeleteNode(state, dispatch, nodeName) {
    const position = state.selection.$anchor;
    for (let depth = position.depth; depth > 0; depth--) {
        const node = position.node(depth);
        if (node.type.name === nodeName) {
            if (dispatch) {
                dispatch(state.tr
                    .delete(position.before(depth), position.after(depth))
                    .scrollIntoView());
            }
            return true;
        }
    }
    return false;
}

function tuiGetElementPoint(x, y, element) {
    const { left, top, width, height } = element.getBoundingClientRect();
    return [tuiClamp(x - left, 0, width) / width, tuiClamp(y - top, 0, height) / height];
}

function tuiGetGradientData(gradient) {
    return gradient
        .slice(0, Math.max(0, gradient.length - 1))
        .replace(`linear-gradient(`, ``);
}

function hasAttributes(markAttributes, attributes) {
    const keys = Object.keys(attributes);
    return (!keys.length ||
        !!keys.filter(key => attributes[key] === markAttributes[key]).length);
}
function findMarkInSet(marks, markType, attributes = {}) {
    return (marks.find(({ attrs, type }) => type === markType && hasAttributes(attrs, attributes)) || null);
}
function tuiGetMarkRange(pos, type, attributes = {}) {
    if (!pos || !type) {
        return null;
    }
    const { node, offset } = pos.parent.childAfter(pos.parentOffset);
    if (!node) {
        return null;
    }
    const mark = findMarkInSet(node.marks, type, attributes);
    if (!mark) {
        return null;
    }
    let startIndex = pos.index();
    let startPos = pos.start() + offset;
    let endIndex = startIndex + 1;
    let endPos = startPos + node.nodeSize;
    findMarkInSet(node.marks, type, attributes);
    while (startIndex > 0 && mark.isInSet(pos.parent.child(startIndex - 1).marks)) {
        startIndex -= 1;
        startPos -= pos.parent.child(startIndex).nodeSize;
    }
    while (endIndex < pos.parent.childCount &&
        !!findMarkInSet(pos.parent.child(endIndex).marks, type, attributes)) {
        endPos += pos.parent.child(endIndex).nodeSize;
        endIndex += 1;
    }
    return {
        from: startPos,
        to: endPos,
    };
}

function tuiGetNestedNodes(node) {
    const nodes = [];
    // @note: the content field is not array type
    node.content.forEach(child => {
        if (child instanceof Node) {
            nodes.push([child.type.name, child.attrs]);
        }
    });
    return nodes;
}

function tuiGetSelectedContent(state, current) {
    const currentNodeContent = current !== null && current !== void 0 ? current : state.selection.$head.parent.textContent;
    const selected = state.doc.cut(state.selection.from, state.selection.to);
    return selected.content.size
        ? getHTMLFromFragment(selected.content, state.schema)
        : currentNodeContent;
}

/**
 * Provides document.execCommand('insertHTML', false, html) support to unsupported browser,
 * taking Undo stack into account if possible
 *
 * @throws Will throw an error if selection could not be retrieved
 *
 * @param doc document to execute on
 * @param html html to be inserted
 */
function tuiInsertHtml(doc, html) {
    if (doc.queryCommandSupported(`insertHTML`)) {
        doc.execCommand(`insertHTML`, false, html);
        return;
    }
    const selection = doc.getSelection();
    if (!selection) {
        throw new TuiDocumentSelectionException();
    }
    doc.execCommand(`ms-beginUndoUnit`);
    const range = selection.getRangeAt(0);
    const documentFragment = range.createContextualFragment(html);
    range.deleteContents();
    range.insertNode(documentFragment);
    doc.execCommand(`ms-endUndoUnit`);
}

/**
 * Provides document.execCommand('insertText', false, text) support to unsupported browser,
 * taking Undo stack into account if possible
 *
 * @throws Will throw an error if selection could not be retrieved
 *
 * @param doc document to execute on
 * @param text text to be inserted
 */
function tuiInsertText(doc, text) {
    if (doc.queryCommandSupported(`insertText`)) {
        doc.execCommand(`insertText`, false, text);
        return;
    }
    const selection = doc.getSelection();
    if (!selection) {
        throw new TuiDocumentSelectionException();
    }
    doc.execCommand(`ms-beginUndoUnit`);
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(doc.createTextNode(text));
    doc.execCommand(`ms-endUndoUnit`);
}

/**
 * Checks if selection is inside a specific selector
 * @param selection
 * @param selector
 * @return true if selection is completely inside a particular selector
 */
function tuiIsSelectionIn({ anchorNode, focusNode }, selector) {
    // Even though focusNode/anchor-node are defined as Node, they can be null on initial nested document query
    return (!!anchorNode &&
        !!focusNode &&
        tuiIsNodeIn(anchorNode, selector) &&
        tuiIsNodeIn(focusNode, selector));
}

/**
 * @description:
 * This converter help you to painlessly migrate old tags to the tui-editor[new]
 */
function tuiLegacyEditorConverter(content) {
    const tree = new DOMParser().parseFromString(content, `text/html`);
    migration(tree.body);
    return tree.body.innerHTML;
}
function migration(element) {
    for (const child of Array.from(element.children)) {
        if (child.children.length) {
            migration(child);
        }
        if (child.tagName === `FONT`) {
            if (child.hasAttribute(`size`)) {
                switch (child.getAttribute(`size`)) {
                    case `6`: {
                        migrateHeading(`h1`, child);
                        break;
                    }
                    case `5`: {
                        migrateHeading(`h2`, child);
                        break;
                    }
                    case `4`: {
                        migrateParagraph(`17px`, child);
                        break;
                    }
                    case `3`: {
                        migrateParagraph(`15px`, child);
                        break;
                    }
                    case `2`: {
                        migrateParagraph(`13px`, child);
                        break;
                    }
                    default:
                        break;
                }
            }
            else if (child.hasAttribute(`color`)) {
                migrateFontHighlight(child);
            }
        }
    }
}
function migrateHeading(selector, element) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const heading = document.createElement(selector);
    heading.innerHTML = element.innerHTML;
    if (((_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.tagName) === `P`) {
        const newRef = (_c = (_b = element.parentElement) === null || _b === void 0 ? void 0 : _b.parentElement) === null || _c === void 0 ? void 0 : _c.insertBefore(heading, element.parentElement);
        (_d = element.parentElement) === null || _d === void 0 ? void 0 : _d.removeChild(element);
        if ((newRef === null || newRef === void 0 ? void 0 : newRef.nextSibling) && !((_f = (_e = newRef === null || newRef === void 0 ? void 0 : newRef.nextSibling) === null || _e === void 0 ? void 0 : _e.textContent) === null || _f === void 0 ? void 0 : _f.trim())) {
            (_h = (_g = newRef.nextSibling) === null || _g === void 0 ? void 0 : _g.parentElement) === null || _h === void 0 ? void 0 : _h.removeChild(newRef.nextSibling);
        }
    }
    else {
        (_j = element.parentElement) === null || _j === void 0 ? void 0 : _j.replaceChild(heading, element);
    }
}
function migrateParagraph(fontSize, element) {
    var _a;
    const p = document.createElement(`p`);
    const span = document.createElement(`span`);
    span.setAttribute(`style`, `font-size: ${fontSize}`);
    span.innerHTML = element.innerHTML;
    p.append(span);
    (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.replaceChild(element.parentElement.tagName === `P` ? span : p, element);
}
function migrateFontHighlight(element) {
    var _a;
    const span = document.createElement(`span`);
    span.setAttribute(`style`, `color: ${element.getAttribute(`color`)}`);
    span.innerHTML = element.innerHTML;
    (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.replaceChild(span, element);
}

//
// TypeScript parser based on Dean Taylor's answer:
// https://stackoverflow.com/a/20238168/2706426
//
// SETUP CODE
const COMMA = `\\s*,\\s*`; // Allow space around comma.
const HEX = `#(?:[a-f0-9]{6}|[a-f0-9]{3})`; // 3 or 6 character form
const RGB = `\\(\\s*(?:\\d{1,3}\\s*,\\s*){2}\\d{1,3}\\s*\\)`; // "(1, 2, 3)"
const RGBA = `\\(\\s*(?:\\d{1,3}\\s*,\\s*){2}\\d{1,3}\\s*,\\s*\\d*\\.?\\d+\\)`; // "(1, 2, 3, 4)"
const VALUE = `(?:[+-]?\\d*\\.?\\d+)(?:%|[a-z]+)?`; // ".9", "-5px", "100%".
const KEYWORD = `[_a-z-][_a-z0-9-]*`; // "red", "transparent", "border-collapse".
const COLOR = [
    `(?:`,
    HEX,
    `|`,
    `(?:rgb|hsl)`,
    RGB,
    `|`,
    `(?:rgba|hsla)`,
    RGBA,
    `|`,
    KEYWORD,
    `)`,
];
const REGEXP_ARRAY = [
    `\\s*(`,
    ...COLOR,
    `)`,
    `(?:\\s+`,
    `(`,
    VALUE,
    `))?`,
    `(?:`,
    COMMA,
    `\\s*)?`,
];
function tuiParseGradient(input) {
    const stopsRegexp = new RegExp(REGEXP_ARRAY.join(``), `gi`);
    const stopsString = input.startsWith(`to`) || input.match(/^\d/)
        ? input.slice(Math.max(0, input.indexOf(`,`) + 1)).trim()
        : input;
    const side = input.startsWith(`to`)
        ? input.split(`,`)[0]
        : `to bottom`;
    let stops = [];
    let matchColorStop = stopsRegexp.exec(stopsString);
    while (matchColorStop !== null) {
        stops = stops.concat({
            color: matchColorStop[1],
            position: getPosition(matchColorStop[2], stops.length),
        });
        matchColorStop = stopsRegexp.exec(stopsString);
    }
    stops = stops.filter(({ color }) => color.startsWith(`#`) || color.startsWith(`rgb`));
    return {
        stops,
        side,
    };
}
function getPosition(match, stops) {
    const fallback = stops === 1 ? `100%` : `${stops}%`;
    return (match === null || match === void 0 ? void 0 : match.includes(`%`)) ? match : fallback;
}

function tuiParseNodeAttributes(attrs) {
    return attrs.reduce((result, attribute) => {
        result[attribute] = {
            parseHTML: element => element === null || element === void 0 ? void 0 : element.getAttribute(`${attribute}`),
        };
        return result;
    }, {});
}

function tuiParseStyle(style) {
    return style
        .split(`;`)
        .reduce((ruleMap, ruleString) => {
        var _a;
        const [left, right] = (_a = ruleString.split(`:`)) !== null && _a !== void 0 ? _a : [];
        if (left && right) {
            ruleMap[left.trim()] = right.trim();
        }
        return ruleMap;
    }, {});
}

function tuiIsSafeLinkRange(range) {
    var _a;
    const textNodeLength = ((_a = range.endContainer.nodeValue) === null || _a === void 0 ? void 0 : _a.length) || 0;
    return (range.endOffset - range.startOffset > 0 ||
        (range.endOffset - range.startOffset === 0 && textNodeLength === 1) ||
        (range.startOffset !== 0 &&
            textNodeLength > 1 &&
            range.endOffset !== textNodeLength));
}

function tuiToGradient({ stops, side }) {
    return `linear-gradient(${side}, ${stops
        .map(({ color, position }) => `rgba(${tuiParseColor(color).join(`, `)}) ${position}`)
        .join(`, `)})`;
}

/**
 * Generated bundle index. Do not edit.
 */

export { tuiDeleteNode, tuiGetElementPoint, tuiGetGradientData, tuiGetMarkRange, tuiGetNestedNodes, tuiGetSelectedContent, tuiInsertHtml, tuiInsertText, tuiIsSafeLinkRange, tuiIsSelectionIn, tuiLegacyEditorConverter, tuiParseGradient, tuiParseNodeAttributes, tuiParseStyle, tuiToGradient };
